import { Text } from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import { ConsentQuestion, Email, NombreApellidos } from 'src/components';
import { FormController, LayoutTemplate, removeWhiteSpace, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { isCalculadoraForm } from 'src/forms';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { ConfirmacionProspectsNoQuestions, ThankYouProspects } from '.';
import { formatFields, formatFlags, formatHeaders, formDataBuilder } from '../format-fields';
import { getCalculadoraData, getDescInterestCarline } from '../Leads';

interface ProspectsFormProps {
	inPage?: boolean;
	customSteps?: Steps[];
	updatedFormData?: Partial<FormDataGroup>;
	updatedFormInfo?: Partial<FormInfo>;
}

export function ProspectsPosventaForm({ inPage, updatedFormData }: ProspectsFormProps) {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();

	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<>
					<Text bold>¿A qué email </Text>te enviamos este presupuesto de mantenimiento?
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<Email required />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'Email',
			outputs: ['email'],
			outputsText: ['email'],
		},
		{
			title: (
				<>
					Genial. Aunque antes de mandártelo, nos gustaría saber <Text bold>quién eres</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<div>
							<NombreApellidos />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			screenIndex: index++,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			screenIndex: index++,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <ConfirmacionProspectsNoQuestions />,
			screenIndex: index++,
			name: 'Confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const NuevoFormData: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: 'VW_DDB_FRM_PROSPECT_POSVENTA',
			FORM_OBJECTIVE: 'Presupuesto Posventa',
			SF_CAMPAIGN_ID: '7011n000000JwcWAAS',
			//DESC_CAMPAIGNBLOCK: '13. VW Posventa Inbound',
			...updatedFormData?.headers,
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
			FLAG_RELACION_MARCA_SIN_IC: 'false',
			...updatedFormData?.flags,
		},
		fields: {
			formName: 'prospect-posventa',
			formType: 'otherform',
			pageCategory: 'Prospect',
			...updatedFormData?.fields,
		}, // Analítica
	};

	const sendForm = (): Promise<boolean> => {
		const { formData, dealersInfo }: OneFormState = store.getState();
		const { fields, headers, flags } = formData;

		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, url, device } = getParamsForm();

			const data = {
				...formatHeaders(headers),
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[FORM_URL]': url,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[FORM_NAME]': `vw-oneform-${fields.formName}`,
				'headers[FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,

				...formatFlags(flags),

				'fields[DPS_BRAND]': 'Volkswagen',
				'fields[PERSON_NAME]': fields.name,
				'fields[PERSON_SURNAME_1]': fields.surname,
				'fields[PERSON_SURNAME_2]': fields.secondSurname,
				'fields[PERSON_EMAIL]': removeWhiteSpace(fields.email),
				'fields[VEH_INTEREST_ID]': fields.modelo == 'Otro' ? '000000' : dealersInfo?.selectedCarline?.code,
				'fields[VEH_INTEREST_MODEL]': getDescInterestCarline(dealersInfo, fields.modelo),
				...formatFields(fields.formFields),

				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
				...getCalculadoraData(formData),
			};

			if (isCalculadoraForm(fields.formName)) {
				delete data['fields[VEH_INTEREST_ID]'];
				delete data['fields[VEH_INTEREST_MODEL]'];
			}

			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formDataInfo = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formDataInfo);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const getFormInfo = (steps: Steps[]): Partial<FormInfo> => {
		const firstStep = steps[0];
		const lastStep = steps[steps.length - 1];
		const stepsHistory = [0];
		const numberOfScreens = steps.length;

		return {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			notALayer: inPage ? true : false,
			formTheme: inPage ? 'main' : 'inverted',
			formType: inPage ? 'inPage' : 'layer',
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps,
			stepsHistory,
			thankYouComponent: <ThankYouProspects />,
		};
	};

	useEffect(() => {
		const formStepsInfo = getFormInfo(steps);

		initializeForm(formStepsInfo, NuevoFormData);
	}, []);

	return <FormController steps={steps} screenType={inPage ? 'full-screen' : 'layer-screen'} />;
}
